import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useJoinTournament } from '../hooks/useJoinTournament';
import GasBanner from '../components/GasBanner';
import './CS2Lobby.css';

const API             = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const REFRESH_FILLING = 5_000;
const REFRESH_ACTIVE  = 30_000;
const MAX_PLAYERS     = 100;

function formatUSDC(raw) {
  if (!raw) return '$0';
  return `$${(Number(raw) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function useCountdown(windowEnd) {
  const [label, setLabel]     = useState('');
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!windowEnd) return;
    function tick() {
      const diff = windowEnd * 1000 - Date.now();
      if (diff <= 0) { setLabel('Window closed'); setExpired(true); return; }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setLabel(`${h}h ${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`);
      setExpired(false);
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [windowEnd]);

  return { label, expired };
}

function CS2Card({ tournament, walletAddress }) {
  const [mode, setMode]               = useState('idle');
  const [steamId, setSteamId]         = useState('');
  const [formError, setFormError]     = useState('');
  const [successData, setSuccessData] = useState(null);
  const { label: timerLabel, expired } = useCountdown(tournament.window_end);
  const { joinTournament, state: joinState, reset: resetJoin } = useJoinTournament();

  const pct = Math.min((tournament.player_count / MAX_PLAYERS) * 100, 100);

  const stepLabel = {
    approving:      'Step 1/3: Approving USDC...',
    joining:        'Step 2/3: Joining on-chain...',
    fetching_stats: 'Step 3/3: Fetching your current stats...',
    recording:      'Step 3/3: Fetching your current stats...',
  }[joinState.step];

  async function handleJoin(e) {
    e.preventDefault();
    if (!steamId.trim()) { setFormError('Steam64 ID is required'); return; }
    setFormError('');
    setMode('tx');
    try {
      const data = await joinTournament({
        tournament,
        gamePlayerId: steamId.trim(),
        gameField:    'steamPlayerId',
      });
      setSuccessData(data);
      setMode('success');
    } catch (err) {
      setFormError(err?.response?.data?.error || err?.message || 'Failed to join. Try again.');
      setMode('form');
    }
  }

  return (
    <div className="cs2-card">
      <div className="cs2-card__game-badge">🔫 CS2 PREMIER</div>

      <div>
        <div className="cs2-card__prize-label">Winner Takes (90%)</div>
        <div className="cs2-card__prize">{formatUSDC(Math.floor(Number(tournament.prize_pool) * 0.9))}</div>
        <div className="cs2-card__prize-pool-sub">Total pool: {formatUSDC(tournament.prize_pool)} · 10% platform fee</div>
      </div>

      <div className="cs2-card__meta">
        <span className="cs2-card__entry">Entry: {formatUSDC(tournament.entry_fee)}</span>
        {tournament.status === 'active' && (
          <span className={`cs2-card__timer${expired ? ' cs2-card__timer--expired' : ''}`}>
            {timerLabel}
          </span>
        )}
        {tournament.status === 'filling' && (
          <span className="cs2-card__badge-filling">● Filling</span>
        )}
      </div>

      {tournament.status === 'active' && (
        <div className="cs2-card__info">
          60 min window · 1 Premier match · most kills wins · instant USDC payout
        </div>
      )}

      {tournament.status === 'filling' && (
        <div className="cs2-card__filling-banner">
          <div className="cs2-card__filling-title">⏳ Waiting for full lobby</div>
          <div className="cs2-card__filling-sub">
            The 60-minute window starts the moment slot 100 fills. Join now and queue your Premier match.
          </div>
        </div>
      )}

      <div>
        <div className="cs2-card__progress-row">
          <span>Players</span>
          <span>{tournament.player_count} / {MAX_PLAYERS}</span>
        </div>
        <div className="cs2-card__bar-track">
          <div className="cs2-card__bar-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {tournament.players && tournament.players.length > 0 && (
        <div className="cs2-card__players">
          <div className="cs2-card__players-label">Enrolled players</div>
          <div className="cs2-card__players-list">
            {tournament.players.map((p, i) => (
              <div key={i} className="cs2-card__player-row">
                <span className="cs2-card__player-handle">{p.handle}</span>
                <span className="cs2-card__player-wallet">{p.wallet}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === 'idle' && (
        <button
          className="cs2-card__join-btn"
          onClick={() => setMode('form')}
          disabled={tournament.player_count >= MAX_PLAYERS || (tournament.status === 'active' && expired)}
        >
          {tournament.player_count >= MAX_PLAYERS ? 'Full'
            : (tournament.status === 'active' && expired) ? 'Closed'
            : 'Join'}
        </button>
      )}

      {(mode === 'form' || mode === 'tx') && (
        <form className="cs2-card__form" onSubmit={handleJoin}>
          <div className="cs2-card__gas-notice">
            ⛽ Need POL for gas?{' '}
            <a
              href="https://app.uniswap.org/swap?chain=polygon&inputCurrency=0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359&outputCurrency=MATIC&exactAmount=0.10&exactField=input"
              target="_blank"
              rel="noopener noreferrer"
              className="cs2-card__gas-link"
            >
              Swap $0.10 USDC → POL →
            </a>
          </div>
          <div className="cs2-card__stats-notice">
            ⚠️ Your Steam profile and game stats must be set to <strong>Public</strong> — otherwise your kills won't be verifiable.{' '}
            <a href="https://steamcommunity.com/my/edit/settings" target="_blank" rel="noopener noreferrer" className="cs2-card__warning-link">
              Fix privacy settings →
            </a>
          </div>
          <input
            className="cs2-card__input"
            placeholder="Steam64 ID (e.g. 76561198XXXXXXXXX)"
            value={steamId}
            onChange={e => setSteamId(e.target.value)}
            disabled={mode === 'tx'}
            autoFocus
          />
          <div className="cs2-card__steam-hint">
            Find your Steam64 ID at{' '}
            <a href="https://steamid.io" target="_blank" rel="noopener noreferrer" className="cs2-card__warning-link">steamid.io</a>
          </div>
          {formError && <span className="cs2-card__form-error">{formError}</span>}
          {mode === 'tx' && stepLabel && (
            <span className="cs2-card__form-step">{stepLabel}</span>
          )}
          <div className="cs2-card__form-row">
            <button
              type="button"
              className="cs2-card__cancel-btn"
              onClick={() => { setMode('idle'); setSteamId(''); setFormError(''); resetJoin(); }}
              disabled={mode === 'tx'}
            >Cancel</button>
            <button type="submit" className="cs2-card__submit-btn" disabled={mode === 'tx'}>
              {mode === 'tx' ? 'Processing...' : 'Confirm'}
            </button>
          </div>
        </form>
      )}

      {mode === 'success' && successData && (
        <div className="cs2-card__success">
          <div className="cs2-card__success-title">✅ All good, you're in!</div>
          <div className="cs2-card__success-detail">
            {successData.tournament.status === 'active' ? (
              <>Match window is live — queue your Premier match now and rack up kills!</>
            ) : (
              <>Waiting for tournament to start ({successData.tournament.player_count}/{successData.tournament.max_players || 100} players).{' '}
                <strong style={{ color: '#FF6B00' }}>Stay ready</strong> — launch CS2 and queue Premier the moment the window opens!
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CS2Lobby({ walletAddress }) {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const ref = new URLSearchParams(location.search).get('ref');
    if (ref) localStorage.setItem('referralCode', ref);
  }, [location.search]);

  const fetchTournaments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tournament/active?game=cs2`);
      setTournaments(data.tournaments);
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load tournaments. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTournaments(); }, [fetchTournaments]);

  useEffect(() => {
    const hasFilling = tournaments.some(t => t.status === 'filling');
    const id = setInterval(fetchTournaments, hasFilling ? REFRESH_FILLING : REFRESH_ACTIVE);
    return () => clearInterval(id);
  }, [tournaments, fetchTournaments]);

  return (
    <div className="cs2-lobby">
      <Helmet>
        <title>CS2 Premier Tournaments — Win USDC for Most Kills | EZGAME</title>
        <meta name="description" content="Join a CS2 Premier kill tournament for $1 USDC. Most kills in a 60-minute window wins 90% of the prize pool, paid instantly on Polygon." />
        <link rel="canonical" href="https://www.ezegames.live/cs2" />
        <meta property="og:type"        content="website" />
        <meta property="og:site_name"   content="EZGAME" />
        <meta property="og:locale"      content="en_US" />
        <meta property="og:title"       content="CS2 Premier Kill Tournaments — Win USDC | EZGAME" />
        <meta property="og:description" content="$1 entry. One Premier match. 90% prize pool to the top CS2 player — paid instantly in USDC on Polygon." />
        <meta property="og:url"         content="https://www.ezegames.live/cs2" />
        <meta property="og:image"       content="https://www.ezegames.live/og-image.png" />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:site"        content="@EZ_Egames" />
        <meta name="twitter:title"       content="CS2 Premier Kill Tournaments — Win USDC | EZGAME" />
        <meta name="twitter:description" content="$1 entry. One Premier match. 90% prize pool to the top CS2 player — paid instantly on Polygon." />
        <meta name="twitter:image"       content="https://www.ezegames.live/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SportsEvent',
          name: 'EZGAME CS2 Premier Kill Tournament',
          description: 'CS2 Premier kill tournament — $1 USDC entry, 60-minute match window, 90% of the prize pool to the player with the most kills.',
          sport: 'Counter-Strike 2',
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
          location: { '@type': 'VirtualLocation', url: 'https://www.ezegames.live/cs2' },
          organizer: { '@type': 'Organization', name: 'EZGAME', url: 'https://www.ezegames.live' },
          offers: { '@type': 'Offer', price: '1.00', priceCurrency: 'USD', availability: 'https://schema.org/InStock', url: 'https://www.ezegames.live/cs2' },
          url: 'https://www.ezegames.live/cs2',
        })}</script>
      </Helmet>

      {/* Hero */}
      <div className="cs2-lobby__hero">
        <div className="cs2-lobby__hero-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/cs2/cs2image1.png)` }} />
        <div className="cs2-lobby__hero-overlay" />
        <div className="cs2-lobby__hero-content">
          <div className="cs2-lobby__eyebrow">🔫 CS2 Premier Tournaments</div>
          <h1 className="cs2-lobby__hero-title">
            One Premier Match.<br /><span className="cs2-gradient-text">Win USDC.</span>
          </h1>
          <p className="cs2-lobby__hero-sub">
            Pay $1 USDC, get the most kills in a 60-minute Premier window, and get paid instantly on Polygon.
            Tiebreaker: K/D ratio, then in-game score. Gas under $0.01. Pure aim.
          </p>
        </div>
      </div>

      {/* How it works strip */}
      <div className="cs2-lobby__how-strip">
        {[
          { icon: '🦊', text: 'Connect wallet' },
          { icon: '💵', text: 'Pay $1 USDC entry' },
          { icon: '🔫', text: 'Play 1 CS2 Premier match — most kills wins' },
          { icon: '⚡', text: 'Instant USDC payout on Polygon' },
        ].map(s => (
          <div key={s.text} className="cs2-lobby__how-item">
            <span className="cs2-lobby__how-icon">{s.icon}</span>
            <span className="cs2-lobby__how-text">{s.text}</span>
          </div>
        ))}
      </div>

      {/* Tournaments */}
      <div className="cs2-lobby__main">
        <GasBanner />
        <div className="cs2-lobby__warning">
          <span className="cs2-lobby__warning-icon">⚠️</span>
          <div>
            <strong>Your Steam profile and CS2 stats must be Public</strong> before joining.
            Private stats = kills not verified = no payout.{' '}
            <a
              href="https://steamcommunity.com/my/edit/settings"
              target="_blank"
              rel="noopener noreferrer"
              className="cs2-lobby__warning-link"
            >Set Steam privacy to Public →</a>
          </div>
        </div>

        <div className="cs2-lobby__header">
          <h2 className="cs2-lobby__title">Active <span className="cs2-gradient-text">Tournaments</span></h2>
          {lastRefresh && (
            <span className="cs2-lobby__refresh">Updated {lastRefresh.toLocaleTimeString()}</span>
          )}
        </div>

        {error   && <div className="cs2-lobby__error">{error}</div>}
        {loading && <div className="cs2-lobby__status">Loading tournaments...</div>}

        {!loading && !error && tournaments.length === 0 && (
          <div className="cs2-lobby__status">
            No active CS2 tournaments right now — CS2 lobbies open April 27.{' '}
            <a href="/#waitlist" className="cs2-lobby__warning-link">Join the waitlist →</a>
          </div>
        )}

        {!loading && tournaments.length > 0 && (
          <div className="cs2-lobby__grid">
            {tournaments.map(t => (
              <CS2Card key={t.id} tournament={t} walletAddress={walletAddress} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
